import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Badge, Card, Col, Row, Spinner } from "react-bootstrap";

import { analytics as getLeadAnalytics, workQueue as getLeadQueue } from "../../../actions/leads";
import { isGuid } from "../../../utils/string";
import { format as formatDate } from "../../../utils/date";

class LeadWidget extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            analytics: null,
            queue: {
                overdue: [],
                today: [],
                upcoming: [],
                unassigned: []
            }
        };
    }

    componentDidMount = async () => {
        await this.loadData();
    };

    loadData = async () => {
        this.setState({ loading: true });

        const [analytics, queue] = await Promise.all([
            this.props.getLeadAnalytics(),
            this.props.getLeadQueue({ mine: true })
        ]);

        this.setState({
            loading: false,
            analytics: analytics?.success ? analytics : null,
            queue: queue?.success ? queue : {
                overdue: [],
                today: [],
                upcoming: [],
                unassigned: []
            }
        });
    };

    openLeads = () => {
        window.location.href = "/dashboard/leads";
    };

    openPerson = (personUuid) => {
        if (!isGuid(personUuid)) return;
        window.location.href = `/dashboard/people/${personUuid}`;
    };

    queueItems = () => {
        const queue = this.state.queue || {};
        return [
            ...(queue.overdue || []).map((item) => ({ ...item, bucket: "Overdue", variant: "danger" })),
            ...(queue.today || []).map((item) => ({ ...item, bucket: "Today", variant: "primary" })),
            ...(queue.upcoming || []).map((item) => ({ ...item, bucket: "Upcoming", variant: "secondary" }))
        ].slice(0, 6);
    };

    renderMetric = (label, value, variant = "secondary") => (
        <Col xs="6" md="3">
            <div className="border rounded p-2 h-100">
                <div className="text-muted small">{label}</div>
                <div className={`h4 mb-0 text-${variant}`}>{value ?? 0}</div>
            </div>
        </Col>
    );

    renderQueueItem = (item) => (
        <div key={item.uuid || item.leadUuid} className="py-2 border-bottom">
            <Row className="g-2 align-items-center">
                <Col xs="4" md="3">
                    <Badge bg={item.variant}>{item.bucket}</Badge>
                </Col>
                <Col xs="8" md="5" className="text-truncate">
                    <button
                        type="button"
                        className="btn btn-link p-0 text-start text-truncate"
                        onClick={() => this.openPerson(item.personUuid)}
                        title={item.personName}
                    >
                        {item.personName || "Lead"}
                    </button>
                    <div className="small text-muted text-truncate">{item.leadSubject || item.subject || ""}</div>
                </Col>
                <Col xs="12" md="4" className="small text-muted text-md-end">
                    {item.dueAt ? `Due ${formatDate(item.dueAt, true)}` : item.leadSource || ""}
                </Col>
            </Row>
        </div>
    );

    renderUnassigned = () => {
        const items = (this.state.queue?.unassigned || []).slice(0, 3);
        if (!items.length) return null;

        return (
            <div className="mt-3">
                <div className="fw-bold small mb-1">Unassigned</div>
                {items.map((item) => (
                    <div key={item.leadUuid} className="small d-flex justify-content-between border-bottom py-1">
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-start text-truncate"
                            onClick={() => this.openPerson(item.personUuid)}
                        >
                            {item.personName || "Lead"}
                        </button>
                        <span className="text-muted ms-2">{item.leadSource || ""}</span>
                    </div>
                ))}
            </div>
        );
    };

    render() {
        const analytics = this.state.analytics || {};
        const queue = this.state.queue || {};
        const items = this.queueItems();

        return (
            <Card style={{ height: "100%" }}>
                <Card.Header>
                    <Row className="align-items-center">
                        <Col>
                            <button
                                type="button"
                                className="btn btn-link p-0 fw-bold"
                                onClick={this.openLeads}
                            >
                                Leads
                            </button>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {this.state.loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <>
                            <Row className="g-2 mb-3">
                                {this.renderMetric("Open", analytics.open, "primary")}
                                {this.renderMetric("Overdue", analytics.overdue, "danger")}
                                {this.renderMetric("Today", (queue.today || []).length, "primary")}
                                {this.renderMetric("No Follow-Up", analytics.noFollowUp, "warning")}
                            </Row>

                            {items.length ? (
                                <div>
                                    {items.map(this.renderQueueItem)}
                                </div>
                            ) : (
                                <div className="text-muted py-3">
                                    No lead follow-ups need attention.
                                </div>
                            )}

                            {this.renderUnassigned()}
                        </>
                    )}
                </Card.Body>
            </Card>
        );
    }
}

LeadWidget.propTypes = {
    auth: PropTypes.object.isRequired,
    getLeadAnalytics: PropTypes.func.isRequired,
    getLeadQueue: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps, {
    getLeadAnalytics,
    getLeadQueue
})(LeadWidget);
